import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PathfindersFlags } from 'src/app/shared/models/enums/team-feature-flags/pathfinders-feature-flags.enum';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { interpolateParams } from 'src/app/shared/utilities/interpolate-params';
import { combine } from 'src/app/shared/utilities/route';
import { Clinician } from '../models/clinician.model';
import { GSA } from '../models/gsa.model';
import { ProfessionSpecialties } from '../models/profession-specialties.model';
import { MarginCalculatorBaseService } from './margin-calculator-base.service';

@Injectable({
    providedIn: 'root'
})
export class MarginCalculatorClinicianService extends MarginCalculatorBaseService {
    private gsaSpotOn = false;

    constructor(
        private readonly _http: HttpClient,
        private readonly _featureManager: LDFeatureManager
    ) {
        super();

        this._featureManager
            .isEnabled(PathfindersFlags.MarginCalculatorGsaSpotOn)
            .subscribe((isEnabled) => (this.gsaSpotOn = isEnabled));
    }

    getClinicianNames(count: number, name = '', includeSubcontractors = false): Observable<Clinician[]> {
        return this._http
            .get<
                Clinician[]
            >(`${this.coreApiPath}/margin-calculator/clinician-names?name=${name}&count=${count}&includeSubcontractors=${includeSubcontractors}`)
            .pipe(
                map((response: Clinician[]) =>
                    response
                        .map((clinician) => {
                            clinician['name'] = `${clinician.firstName} ${clinician.lastName}`;
                            return clinician;
                        })
                        .sort((first, second) => (first['name'].toLowerCase() < second['name'].toLowerCase() ? -1 : 1))
                )
            );
    }

    getClinicianById(clinicianId: number): Observable<Clinician> {
        return this._http
            .get<Clinician>(`${this.coreApiPath}/margin-calculator/clinician-info/${clinicianId}`)
            .pipe(map((response) => response));
    }

    getClinicianEmails(count: number, email = ''): Observable<Clinician[]> {
        return this._http
            .get<Clinician[]>(`${this.coreApiPath}/margin-calculator/clinician-emails?email=${email}&count=${count}`)
            .pipe(map((response) => response.sort((first, second) => (first.email < second.email ? -1 : 1))));
    }

    getGSALimits(hospitalId: number, startDate: string, endDate: string): Observable<GSA> {
        if (this.gsaSpotOn) {
            const url = combine(this.coreApiPath, 'margin-calculator/per-diems/gsa-rates');
            const params = interpolateParams({ hospitalId, startDate, endDate });
            return this._http.get<GSA>(url, { params });
        }

        return this._http
            .get<GSA>(
                `${this.coreApiPath}/margin-calculator/gsa-rates?hospitalId=${hospitalId}&startDate=${startDate}&endDate=${endDate}`
            )
            .pipe(map((response) => response));
    }

    getProfessionWithSpecialties(professionId: number): Observable<ProfessionSpecialties> {
        const url = combine(this.coreApiPath, `margin-calculator/profession-specialties/${professionId}`);
        return this._http.get<ProfessionSpecialties>(url);
    }
}
